/**
 * The permission codes the housekeeping back office declares an interest in.
 *
 * These are the dot-strings `internal/pms/permissions.go` registers (C9 §6.1's
 * housekeeping family, six codes) and that arrive on `/auth/me`. Nothing here is
 * a security boundary — the server checks the route and then checks again in the
 * service layer — but an action a user cannot perform should not be rendered
 * disabled, it should not be rendered at all (CLAUDE.md §11).
 *
 * **One of C9's four segregation-of-duties pairs runs through these screens, and
 * no control here ever infers one half from the other:**
 *
 *  - **`pms.housekeeping.status_set` ⊗ `pms.housekeeping.inspect`.** The
 *    attendant who cleaned a room must not be the one who certifies it. So the
 *    room-status grid renders the three settable states behind `status_set`, and
 *    "Inspect" behind `inspect`, and a user holding one never sees the other.
 *    The overridable rule `pms.housekeeping.inspect_own_clean` is the escape
 *    hatch for a one-person night shift; it is the *server's* control, and the
 *    grid surfaces its refusal rather than pre-empting it, because only the
 *    server knows who set the room clean.
 *
 * `pms.room.out_of_order` belongs to the masters family rather than the
 * housekeeping one, and it is listed because the out-of-order calendar is a
 * housekeeping screen guarded by a masters permission — the kind of mismatch
 * that gets a screen guarded by the wrong code when it is left implicit.
 */

export const HOUSEKEEPING_PERMISSIONS = {
  /** Boards, tasks, faults and the room-status grid are all readable behind it. */
  read: 'pms.housekeeping.read',
  /** Generating, creating, rebalancing and releasing a board. */
  assign: 'pms.housekeeping.assign',
  /** Moving a room through dirty → cleaning → clean. */
  statusSet: 'pms.housekeeping.status_set',
  /** SoD partner of `statusSet`: certifying a cleaned room. */
  inspect: 'pms.housekeeping.inspect',
  /** Raising a fault from the back office, as a tablet does from the floor. */
  faultReport: 'pms.fault.report',
  /** Triaging and resolving one — the act that opens and releases an OOO row. */
  faultTriage: 'pms.fault.triage',
} as const

/** Codes owned by other C9 families that these screens read through. */
export const HOUSEKEEPING_EXTERNAL_PERMISSIONS = {
  /** The out-of-order calendar writes `pms_room_out_of_order`, a masters table. */
  roomOutOfOrder: 'pms.room.out_of_order',
  /** Every screen is scoped to a property, and the picker needs its list. */
  propertyRead: 'pms.property.read',
} as const

export type HousekeepingPermission =
  (typeof HOUSEKEEPING_PERMISSIONS)[keyof typeof HOUSEKEEPING_PERMISSIONS]
